/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Alert, Button, Cell, DataTable, FormLabel, Grid, Heading, HFlow, Text, Tooltip, VFlow } from 'bold-ui'
import { Box } from 'components/Box'
import { DateTime } from 'components/date'
import {
  CheckboxField,
  DateField,
  FormPrompt,
  NumberField,
  parse,
  RadioField,
  TextAreaField,
  TextField,
} from 'components/form'
import { TipoReceitaSelectField } from 'components/form/field/select/TipoReceitaSelectField'
import { ViaAdministracaoSelectField } from 'components/form/field/select/ViaAdministracaoSelectField'
import theme from 'config/theme'
import {
  useConexaoHorusLazyQuery,
  useConsultaEstoqueHorusLazyQuery,
  useConsultaEstoqueHorusSincronizadoSubscription,
} from 'graphql/hooks.generated'
import {
  ConexaoHorusQuery,
  DisponibilidadeHorusEnum,
  TipoFrequencia,
  TurnoEnum,
  UnidadeMedidaTempoEnum,
} from 'graphql/types.generated'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FormRenderProps } from 'react-final-form'
import { tipoReceitaRecord } from 'types/enums'
import { metaPath } from 'util/metaPath'
import { v4 as uuidv4 } from 'uuid'

import DisponibilidadeHorus from '../components/DisponibilidadeHorus'
import FrequenciaTabs from '../components/FrequenciaTabs'
import MedicamentoNaoCadastradoSection from '../components/MedicamentoNaoCadastradoSection'
import MedicamentoSection from '../components/MedicamentoSection'
import { MedicamentoFormModel } from '../MedicamentoFormModel'
import { createPosologiaLabel, durationCalculator, enumToStringDisponibilidadeHorus } from '../utils/prescricaoUtils'

const path = metaPath<MedicamentoFormModel>()

export interface ConsultaHorusItemType {
  quantidadeDisponivel: number
  disponibilidade: DisponibilidadeHorusEnum
  unidadeSaude: {
    id: string
    nome?: string
  }
}

export interface AdicionarMedicamentoFormPanelProps extends FormRenderProps<MedicamentoFormModel> {
  isEdit: boolean
  isReplicar: boolean
  onClearEditedMedicamento(newEditedValue: MedicamentoFormModel): void
  medicamentoToEdit: MedicamentoFormModel
}

export default function AdicionarMedicamentoFormPanel(props: AdicionarMedicamentoFormPanelProps) {
  const { values, form, handleSubmit, isEdit, isReplicar, onClearEditedMedicamento, medicamentoToEdit } = props
  const [consultaEstoqueHorusLazyQuery] = useConsultaEstoqueHorusLazyQuery()
  const [hashConsultaHorus, setHashConsultaHorus] = useState<string>()
  const [horusData, setHorusData] = useState<ConsultaHorusItemType[]>()
  const [statusConsulta, setStatusConsulta] = useState<DisponibilidadeHorusEnum>()
  const [isHorusLoading, setIsHorusLoading] = useState(false)
  const [statusConexao, setStatusConexao] = useState<ConexaoHorusQuery>()
  const [executeConexaoQuery, { loading: loadingConexaoInternet }] = useConexaoHorusLazyQuery({
    onCompleted: (dataConexao) => setStatusConexao(dataConexao),
  })

  useEffect(() => {
    form.mutators.setValue(
      path.dataFimTratamento.alias,
      durationCalculator(values.duracao, values.dataInicioTratamento, values.escalaDuracao)
    )
  }, [form.mutators, values.dataInicioTratamento, values.duracao, values.escalaDuracao])

  useEffect(() => {
    form.mutators.setValue(
      path.posologia.alias,
      createPosologiaLabel(
        values.tipoFrequencia,
        values.dose,
        values.intervaloDose,
        values.frequenciaDose,
        values.quantidadePeriodoFrequenciaTurno,
        values.unidadeMedidaTempoFrequenciaTurno,
        values.turno,
        values.doseUnica
      )
    )
  }, [
    form.mutators,
    values.dose,
    values.doseUnica,
    values.frequenciaDose,
    values.intervaloDose,
    values.quantidadePeriodoFrequenciaTurno,
    values.tipoFrequencia,
    values.turno,
    values.unidadeMedidaTempoFrequenciaTurno,
  ])

  const handleOnReset = useCallback(
    (registroManual: boolean) => {
      if (isEdit) {
        const id: ID = values.id
        onClearEditedMedicamento({
          id,
          registroManual,
          dataInicioTratamento: parse(new Date()),
          escalaDuracao: UnidadeMedidaTempoEnum.DIAS,
          tipoFrequencia: TipoFrequencia.INTERVALO,
        })
        form.getRegisteredFields().forEach((field) => form.resetFieldState(field))
      } else {
        form.reset()
        form.getRegisteredFields().forEach((field) => form.resetFieldState(field))
        form.mutators.setValue(path.registroManual.alias, registroManual)
      }
      setHorusData(null)
    },
    [form, isEdit, onClearEditedMedicamento, values.id]
  )

  useEffect(() => {
    executeConexaoQuery()
    if (statusConexao) {
      if (statusConexao?.conexao?.internet.habilitado) {
        if (statusConexao?.conexao?.horus.habilitado) {
          if (values.principioAtivoCombo?.principioAtivo) {
            const hash = uuidv4()
            setHashConsultaHorus(hash)
            setIsHorusLoading(true)
            consultaEstoqueHorusLazyQuery({
              variables: {
                filter: {
                  principioAtivo: values.principioAtivoCombo.principioAtivo.nome,
                  quantidade: values.quantidade,
                  codCatMat: values.principioAtivoCombo.codigoRename ?? values.principioAtivoCombo.codigoCatMat,
                  uuid: hash,
                },
              },
            })
          }
        } else {
          setStatusConsulta(DisponibilidadeHorusEnum.DESABILITADO)
        }
      } else {
        setStatusConsulta(DisponibilidadeHorusEnum.SEM_CONEXAO)
      }
    }
  }, [consultaEstoqueHorusLazyQuery, executeConexaoQuery, statusConexao, values.principioAtivoCombo, values.quantidade])

  useEffect(() => {
    if (values.isVersaoAnterior) {
      form.getRegisteredFields().forEach((field) => form.blur(field))
    }
  }, [form, values.isVersaoAnterior])

  useConsultaEstoqueHorusSincronizadoSubscription({
    onSubscriptionData: (options) => {
      const data = options?.subscriptionData?.data?.consultaEstoqueHorusSincronizado
      if (data?.uuid === hashConsultaHorus) {
        setStatusConsulta(data.validadeConexao)
        setIsHorusLoading(false)
        setHorusData(data.unidadesSaudeConsulta)
        form.mutators.setValue(path.disponibilidade.alias, data?.unidadesSaudeConsulta[0]?.disponibilidade)
      }
    },
  })

  const handleOnCheckMedicamentoNaoCadastrado = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked: boolean = e.target.checked
      handleOnReset(checked)
      setHorusData(null)
    },
    [handleOnReset]
  )

  const handleOnChangeEscalaDuracao = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.value === UnidadeMedidaTempoEnum.INDETERMINADO) {
        form.mutators.setValue(path.duracao.alias, null)
      }
    },
    [form.mutators]
  )

  const handleOnChangeDoseUnica = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        form.mutators.setValue(path.usoContinuo.alias, false)
        form.mutators.setValue(path.turno.alias, null)
        form.mutators.setValue(path.duracao.alias, 1)
        form.mutators.setValue(path.escalaDuracao.alias, UnidadeMedidaTempoEnum.DIAS)
        form.mutators.setValue(path.frequenciaDose.alias, null)
        form.mutators.setValue(path.quantidadePeriodoFrequenciaTurno.alias, null)
        form.mutators.setValue(path.unidadeMedidaTempoFrequenciaTurno.alias, null)
        form.mutators.setValue(path.intervaloDose.alias, null)
      } else {
        form.mutators.setValue(path.duracao.alias, null)
        if (values.tipoFrequencia !== TipoFrequencia.INTERVALO) {
          form.mutators.setValue(path.quantidadePeriodoFrequenciaTurno.alias, 1)
          form.mutators.setValue(path.unidadeMedidaTempoFrequenciaTurno.alias, UnidadeMedidaTempoEnum.DIAS)
        }
        if (values.tipoFrequencia === TipoFrequencia.TURNO) {
          form.mutators.setValue(path.turno.alias, TurnoEnum.MANHA)
        }
      }
    },
    [form.mutators, values.tipoFrequencia]
  )

  const handleOnChangeUsoContinuo = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        if (values.doseUnica) {
          form.mutators.setValue(path.duracao.alias, null)
        }
        if (values.tipoFrequencia !== TipoFrequencia.INTERVALO) {
          form.mutators.setValue(path.quantidadePeriodoFrequenciaTurno.alias, 1)
          form.mutators.setValue(path.unidadeMedidaTempoFrequenciaTurno.alias, UnidadeMedidaTempoEnum.DIAS)
        }
        if (values.tipoFrequencia === TipoFrequencia.TURNO) {
          form.mutators.setValue(path.turno.alias, TurnoEnum.MANHA)
        }
        form.mutators.setValue(path.doseUnica.alias, false)
      }
    },
    [form.mutators, values.doseUnica, values.tipoFrequencia]
  )
  return useMemo(
    () => (
      <VFlow
        style={css`
          margin-top: 1rem;
        `}
        vSpacing={2}
      >
        <FormPrompt condition={!isReplicar && isEdit} />
        <Grid>
          <Cell size={12}>
            <Tooltip text={!values.registroManual && 'Utilize esta opção para preencher as informações livremente'}>
              <span>
                <CheckboxField
                  name={path.registroManual}
                  label='Preencher manualmente. Medicamento não encontrado na lista.'
                  onChange={handleOnCheckMedicamentoNaoCadastrado}
                  data-testid='RegistroManualCheckbox'
                />
              </span>
            </Tooltip>
          </Cell>
          {values.registroManual ? (
            <MedicamentoNaoCadastradoSection path={path} />
          ) : (
            <MedicamentoSection path={path} values={values} />
          )}
          <Cell size={6} data-testid='ViaAdministracao'>
            <ViaAdministracaoSelectField
              name={path.viaAdministracao}
              label='Via de administração'
              required
              data-testid='ViaAdministracaoCombobox'
            />
          </Cell>
          {values.registroManual ? (
            <Cell size={6} data-testid='TipoReceita'>
              <TipoReceitaSelectField
                name={path.tipoReceita}
                label='Tipo de receita'
                required
                data-testid='TipoReceitaCombobox'
              />
            </Cell>
          ) : (
            <Cell size={6}>
              <VFlow vSpacing={0.2}>
                <Text fontWeight='bold'>Tipo de receita</Text>
                <Text data-testid='TipoReceitaLabel'>
                  {tipoReceitaRecord[values?.principioAtivoCombo?.principioAtivo?.listaMaterial?.tipoReceita] ?? '-'}
                </Text>
              </VFlow>
            </Cell>
          )}
          {values.isVersaoAnterior && (
            <Cell size={12}>
              <Alert type='info' inline={true}>
                <Text fontWeight='bolder' color='primary'>
                  Posologia registrada em versão anterior:
                </Text>
                {` ${medicamentoToEdit?.posologia}`}
              </Alert>
            </Cell>
          )}
          <Cell size={6} data-testid='Dose'>
            <HFlow>
              <TextField
                name={path.dose}
                label='Dose'
                required
                maxLength={50}
                style={styles.doseInput}
                data-testid='DoseTextField'
              />
            </HFlow>
          </Cell>
          {!values.registroManual && (
            <Cell size={3} data-testid='FormaFarmaceutica'>
              <VFlow vSpacing={0.2}>
                <Text fontWeight='bold'>Forma farmacêutica</Text>
                <Text data-testid='FormaFarmaceuticaLabel'>
                  {values?.principioAtivoCombo?.medicamento?.formaFarmaceutica?.nome ?? '-'}
                </Text>
              </VFlow>
            </Cell>
          )}
          <Cell
            size={3}
            style={css`
              margin-top: 1.4rem;
            `}
            alignSelf='center'
          >
            <CheckboxField
              name={path.doseUnica}
              label='Dose única'
              onChange={handleOnChangeDoseUnica}
              data-testid='DoseUnicaCheckbox'
            />
          </Cell>
          <FrequenciaTabs doseUnica={values.doseUnica} onChange={form.mutators.setValue} />
          <Cell size={12}>
            <VFlow vSpacing={0.6}>
              <Heading level={4}>Posologia</Heading>
              <HFlow>
                <Box style={styles.posologiaBox}>
                  <Text fontWeight='bold' data-testid='PosologiaBox'>
                    {!!values?.posologia?.trim() ? values.posologia : 'Nenhuma informação adicionada'}
                  </Text>
                </Box>
              </HFlow>
            </VFlow>
          </Cell>
          <Cell size={3} data-testid='InicioTratamento'>
            <DateField
              name={path.dataInicioTratamento}
              label='Início do tratamento'
              minDate={new Date()}
              required
              data-testid='InicioTratamentoDateField'
            />
          </Cell>
          <Cell size={2} data-testid='Duracao'>
            <HFlow>
              <Tooltip text={values.doseUnica && 'Medicamento definido como dose única'}>
                <NumberField
                  name={path.duracao}
                  label='Duração'
                  maxLength={3}
                  disabled={values.escalaDuracao === UnidadeMedidaTempoEnum.INDETERMINADO || values.doseUnica}
                  required={!values.doseUnica}
                  data-testid='DuracaoTextField'
                />
              </Tooltip>
            </HFlow>
          </Cell>
          <Cell
            size={3}
            style={css`
              margin-top: 1.5rem;
            `}
          >
            <Grid wrap>
              <Cell size={6}>
                <RadioField
                  name={path.escalaDuracao}
                  value={UnidadeMedidaTempoEnum.DIAS}
                  label='Dia(s)'
                  disabled={values.doseUnica}
                  onChange={handleOnChangeEscalaDuracao}
                  data-testid='DuracaoDiasRadioButton'
                />
              </Cell>
              <Cell size={6}>
                <RadioField
                  name={path.escalaDuracao}
                  value={UnidadeMedidaTempoEnum.SEMANAS}
                  label='Semana(s)'
                  disabled={values.doseUnica}
                  onChange={handleOnChangeEscalaDuracao}
                  data-testid='DuracaoSemanasRadioButton'
                />
              </Cell>
              <Cell size={6}>
                <RadioField
                  name={path.escalaDuracao}
                  value={UnidadeMedidaTempoEnum.MESES}
                  label='Mês(es)'
                  disabled={values.doseUnica}
                  onChange={handleOnChangeEscalaDuracao}
                  data-testid='DuracaoMesesRadioButton'
                />
              </Cell>
              <Cell size={6}>
                <RadioField
                  name={path.escalaDuracao}
                  value={UnidadeMedidaTempoEnum.INDETERMINADO}
                  label='Indeterminada'
                  disabled={values.doseUnica}
                  data-testid='DuracaoIndeterminadoRadioButton'
                />
              </Cell>
            </Grid>
          </Cell>
          <Cell
            size={2}
            style={css`
              margin-left: 4.5rem;
            `}
          >
            <VFlow vSpacing={0} data-testid='ConclusaoLabel'>
              <Text fontWeight='bold'>Conclusão</Text>
              {values.duracao &&
              values.escalaDuracao !== UnidadeMedidaTempoEnum.INDETERMINADO &&
              values.dataFimTratamento ? (
                <DateTime value={values.dataFimTratamento} format='DD/MM/YYYY' />
              ) : (
                <Text>-</Text>
              )}
            </VFlow>
          </Cell>
          <Cell size={12}>
            <CheckboxField
              name={path.usoContinuo}
              label='Uso contínuo'
              data-testid='UsoContinuoCheckbox'
              onChange={handleOnChangeUsoContinuo}
            />
          </Cell>
          <Cell size={12} data-testid='Recomendacoes'>
            <TextAreaField
              style={styles.recomendacoesTextArea}
              name={path.recomendacoes}
              label='Recomendações'
              maxLength={500}
              data-testid='RecomendacoesTextArea'
            />
          </Cell>
          {(values.principioAtivoCombo || horusData) && (
            <Cell size={12}>
              <FormLabel label='Integração com Hórus' />
            </Cell>
          )}
          <Cell
            size={2}
            style={css`
              margin-right: 10rem;
            `}
            data-testid='Quantidade'
          >
            <NumberField
              name={path.quantidade}
              label='Quantidade'
              required
              maxLength={3}
              data-testid='QuantidadeTextField'
            />
          </Cell>
          <Cell
            size={6}
            style={css`
              text-align: left;
            `}
            data-testid='UnidadadeFornecimento'
          >
            {!values.registroManual ? (
              <VFlow
                vSpacing={0}
                style={css`
                  margin-top: 1.25rem;
                `}
              >
                <Text fontWeight='bold'>Unidade de fornecimento</Text>
                <Text data-testid='UnidadadeFornecimentoLabel'>
                  {values?.principioAtivoCombo?.unidadeFornecimento?.nome ?? '-'}
                </Text>
              </VFlow>
            ) : (
              <TextField
                name={path.unidadeFornecimento}
                label='Unidade de fornecimento'
                required
                minLength={2}
                maxLength={100}
                data-testid='UnidadadeFornecimentoTextField'
              />
            )}
          </Cell>
          {!values.registroManual && statusConsulta === DisponibilidadeHorusEnum.SUCESSO_CONEXAO && horusData && (
            <Cell
              size={12}
              style={css`
                margin-top: 2rem;
              `}
            >
              <DataTable<ConsultaHorusItemType>
                rows={horusData}
                loading={isHorusLoading}
                columns={[
                  {
                    name: 'estabelecimento',
                    header: 'Estabelecimento',
                    render: (item) => item?.unidadeSaude?.nome,
                    style: css`
                      width: 35%;
                    `,
                  },
                  {
                    name: 'disponibilidade',
                    header: 'Disponibilidade',
                    render: (item) => item?.quantidadeDisponivel,
                  },
                  {
                    name: 'status',
                    header: 'Status',
                    render: (item) => <DisponibilidadeHorus disponibilidade={item?.disponibilidade} isOnModal />,
                  },
                ]}
              />
            </Cell>
          )}
          {values.registroManual && values.principioAtivoText && (
            <Cell
              size={12}
              style={css`
                text-align: center;
                margin-top: 2rem;
              `}
            >
              <Text fontWeight='bold'>
                {enumToStringDisponibilidadeHorus[DisponibilidadeHorusEnum.DESABILITADO_MANUAL]}
              </Text>
            </Cell>
          )}
          {!values.registroManual &&
            values.principioAtivoCombo &&
            statusConsulta &&
            statusConsulta !== DisponibilidadeHorusEnum.SUCESSO_CONEXAO && (
              <Cell
                size={12}
                style={css`
                  text-align: center;
                  margin-top: 2rem;
                `}
              >
                {statusConsulta === DisponibilidadeHorusEnum.DESABILITADO ? (
                  <Text fontWeight='bold'>
                    {enumToStringDisponibilidadeHorus[DisponibilidadeHorusEnum.DESABILITADO]}
                  </Text>
                ) : (
                  <HFlow alignItems='center'>
                    <Button
                      kind='primary'
                      size='small'
                      loading={loadingConexaoInternet}
                      onClick={() => executeConexaoQuery()}
                    >
                      Tentar novamente
                    </Button>
                    <DisponibilidadeHorus disponibilidade={DisponibilidadeHorusEnum.SEM_CONEXAO} isOnModal />
                  </HFlow>
                )}
              </Cell>
            )}
          <Cell
            size={12}
            style={css`
              margin-top: 1.5rem;
            `}
          >
            <HFlow justifyContent='flex-end'>
              <Button
                onClick={() => handleOnReset(values.registroManual)}
                size='small'
                data-testid='LimparCamposButton'
              >
                Limpar campos
              </Button>
              <Button kind='primary' size='small' onClick={handleSubmit} data-testid='SalvarButton'>
                Salvar prescrição
              </Button>
            </HFlow>
          </Cell>
        </Grid>
      </VFlow>
    ),
    [
      executeConexaoQuery,
      form.mutators.setValue,
      handleOnChangeDoseUnica,
      handleOnChangeEscalaDuracao,
      handleOnChangeUsoContinuo,
      handleOnCheckMedicamentoNaoCadastrado,
      handleOnReset,
      handleSubmit,
      horusData,
      isEdit,
      isHorusLoading,
      isReplicar,
      loadingConexaoInternet,
      medicamentoToEdit,
      statusConsulta,
      values,
    ]
  )
}

const styles = {
  posologiaBox: css`
    min-height: 2.65rem;
    padding: 0.625rem 1rem;
    background: ${theme.pallete.status.info.background};
    border: 1px solid ${theme.pallete.status.info.main};
  `,
  recomendacoesTextArea: css`
    height: 5rem;
    min-height: 3rem;
    max-height: 10rem;
    resize: vertical;
  `,
  doseInput: css`
    width: 21.5rem;
  `,
}
